import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Shield, 
  CheckCircle2, 
  BookOpen, 
  FileText, 
  Calculator,
  Scale,
  Globe,
  Info,
  AlertCircle
} from "lucide-react";

export default function ConformiteOHADA() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConformite();
  }, []);

  const fetchConformite = async () => {
    try {
      const response = await apiClient.get("/comptabilite/conformite-ohada");
      setData(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des informations OHADA");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-zinc-500">
        Impossible de charger les informations de conformité
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="conformite-ohada-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white tracking-tight">
            Conformité OHADA
          </h1>
          <p className="text-zinc-500 mt-1">
            Documentation de conformité au système comptable OHADA
          </p>
        </div>
        <Badge className={`text-lg px-4 py-2 ${
          data.conformite.statut === "CONFORME" 
            ? "bg-emerald-600/20 text-emerald-400 border-emerald-600/30" 
            : "bg-amber-600/20 text-amber-400 border-amber-600/30"
        }`}>
          <CheckCircle2 className="w-5 h-5 mr-2" />
          {data.conformite.statut}
        </Badge>
      </div>

      {/* Statut de conformité */}
      <Card className="bg-gradient-to-r from-emerald-900/20 to-blue-900/20 border-emerald-800/50">
        <CardContent className="p-6">
          <div className="grid md:grid-cols-4 gap-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-emerald-600/20 rounded-lg">
                <Shield className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Version OHADA</p>
                <p className="text-white font-semibold">{data.conformite.version_ohada}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-600/20 rounded-lg">
                <Globe className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Zone d'application</p>
                <p className="text-white font-semibold">{data.conformite.zone_application}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-600/20 rounded-lg">
                <Calculator className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Régime fiscal</p>
                <p className="text-white font-semibold">{data.fiscalite_gabon.regime_actuel}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-600/20 rounded-lg">
                <Scale className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Taux TPS</p>
                <p className="text-white font-semibold">{data.fiscalite_gabon.tps_rate}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Note fiscale */}
      <Card className="bg-blue-900/20 border-blue-800/50">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <p className="text-blue-300 text-sm">{data.fiscalite_gabon.note}</p>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="plan" className="space-y-4">
        <TabsList className="bg-zinc-900/50 border border-zinc-800">
          <TabsTrigger value="plan" className="data-[state=active]:bg-zinc-800">
            <BookOpen className="w-4 h-4 mr-2" />
            Plan Comptable
          </TabsTrigger>
          <TabsTrigger value="journaux" className="data-[state=active]:bg-zinc-800">
            <FileText className="w-4 h-4 mr-2" />
            Journaux
          </TabsTrigger>
          <TabsTrigger value="etats" className="data-[state=active]:bg-zinc-800">
            <Calculator className="w-4 h-4 mr-2" />
            États Financiers
          </TabsTrigger>
          <TabsTrigger value="principes" className="data-[state=active]:bg-zinc-800">
            <Scale className="w-4 h-4 mr-2" />
            Principes
          </TabsTrigger>
          <TabsTrigger value="justificatifs" className="data-[state=active]:bg-zinc-800">
            <Shield className="w-4 h-4 mr-2" />
            Audit
          </TabsTrigger>
        </TabsList>

        {/* Plan Comptable */}
        <TabsContent value="plan">
          <Card className="bg-[#121214] border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">Plan Comptable OHADA</CardTitle>
              <CardDescription>{data.plan_comptable.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Classes */}
              <div className="grid md:grid-cols-2 gap-4">
                {data.plan_comptable.classes_utilisees.map((classe) => (
                  <div key={classe.classe} className="p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge className="bg-blue-600/20 text-blue-400 border-blue-600/30">
                        Classe {classe.classe}
                      </Badge>
                      <span className="text-white font-semibold">{classe.libelle}</span>
                    </div>
                    <p className="text-zinc-400 text-sm">{classe.description}</p>
                  </div>
                ))}
              </div>

              {/* Comptes */}
              <div>
                <h3 className="text-white font-semibold mb-3">
                  Comptes utilisés ({data.plan_comptable.nombre_comptes} comptes)
                </h3>
                <div className="bg-zinc-900/30 rounded-lg border border-zinc-800 max-h-[400px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800">
                        <TableHead className="text-zinc-400 w-[120px]">Numéro</TableHead>
                        <TableHead className="text-zinc-400">Libellé</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(data.plan_comptable.comptes)
                        .sort((a, b) => a[0].localeCompare(b[0]))
                        .map(([numero, libelle]) => (
                          <TableRow key={numero} className="border-zinc-800">
                            <TableCell className="font-mono text-blue-400">{numero}</TableCell>
                            <TableCell className="text-zinc-300">{libelle}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Journaux */}
        <TabsContent value="journaux">
          <Card className="bg-[#121214] border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">Journaux Comptables</CardTitle>
              <CardDescription>Livres comptables utilisés pour l'enregistrement des opérations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {data.journaux_comptables.map((journal) => (
                  <div key={journal.code} className="p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge className="bg-emerald-600/20 text-emerald-400 border-emerald-600/30 font-mono">
                        {journal.code}
                      </Badge>
                      <span className="text-white font-semibold">{journal.libelle}</span>
                    </div>
                    <p className="text-zinc-400 text-sm">{journal.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* États Financiers */}
        <TabsContent value="etats">
          <Card className="bg-[#121214] border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">États Financiers</CardTitle>
              <CardDescription>Documents comptables et rapports disponibles</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {data.etats_financiers.map((etat, index) => (
                  <div 
                    key={index} 
                    className={`p-4 rounded-lg border ${
                      etat.disponible 
                        ? 'bg-emerald-900/10 border-emerald-800/50' 
                        : 'bg-zinc-900/50 border-zinc-800'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {etat.disponible ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-zinc-500" />
                        )}
                        <div>
                          <span className="text-white font-semibold">{etat.nom}</span>
                          <p className="text-zinc-400 text-sm">{etat.description}</p>
                        </div>
                      </div>
                      <Badge className={
                        etat.disponible 
                          ? "bg-emerald-600/20 text-emerald-400" 
                          : "bg-zinc-800 text-zinc-400"
                      }>
                        {etat.disponible ? "Disponible" : etat.note || "À venir"}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Principes Comptables */}
        <TabsContent value="principes">
          <Card className="bg-[#121214] border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">Principes Comptables Appliqués</CardTitle>
              <CardDescription>Fondements du système comptable OHADA respectés par l'application</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {data.principes_comptables_appliques.map((principe, index) => (
                  <div key={index} className="p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-white font-semibold">{principe.principe}</span>
                        <p className="text-zinc-400 text-sm mt-1">{principe.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit et Justificatifs */}
        <TabsContent value="justificatifs">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-[#121214] border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Pièces Justificatives</CardTitle>
                <CardDescription>{data.documents_justificatifs.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {data.documents_justificatifs.types.map((type, index) => (
                    <li key={index} className="flex items-center gap-2 text-zinc-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      {type}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-[#121214] border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Piste d'Audit</CardTitle>
                <CardDescription>{data.audit_trail.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {data.audit_trail.fonctionnalites.map((fonc, index) => (
                    <li key={index} className="flex items-center gap-2 text-zinc-300">
                      <Shield className="w-4 h-4 text-blue-400" />
                      {fonc}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Footer */}
      <Card className="bg-zinc-900/30 border-zinc-800">
        <CardContent className="p-4 text-center">
          <p className="text-zinc-500 text-sm">
            Ce document atteste que l'application respecte les normes du Système Comptable OHADA révisé.
            <br />
            Date de mise en conformité : {data.conformite.date_mise_en_conformite}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
